// Team entity ported from Java Team.java

import { GAME_CONSTANTS } from '../data/constants';
import { GladiatorData, isHealthy } from './Gladiator';
import { Race } from '../data/races';

export interface TeamData {
  id: string;
  name: string;
  money: number;
  rating: number;
  arenaPoints: number;
  wins: number;
  losses: number;
  division: number;

  // Player reference (null = AI controlled)
  playerId: string | null;

  // Gladiator roster
  gladiatorIds: string[];

  // Battle squad (up to 6 gladiators)
  battleGladiatorIds: (string | null)[];

  // Season tracking
  battleResults: number[]; // 12 months, 0=none, 1=win, 2=lose
  addedToBattle: boolean;

  // Monthly finances
  currentMonthBalance: number;
  currentMonthIncome: number;
}

export const BATTLE_RESULT = {
  NONE: 0,
  WIN: 1,
  LOSE: 2,
} as const;

// Create a new team
export function createTeam(name: string, playerId: string | null = null): TeamData {
  return {
    id: `team-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name,
    money: 0,
    rating: GAME_CONSTANTS.START_RATING,
    arenaPoints: GAME_CONSTANTS.START_ARENA_POINTS,
    wins: 0,
    losses: 0,
    division: 3, // Start in lowest division
    playerId,
    gladiatorIds: [],
    battleGladiatorIds: [null, null, null, null, null, null],
    battleResults: new Array(12).fill(BATTLE_RESULT.NONE),
    addedToBattle: false,
    currentMonthBalance: 0,
    currentMonthIncome: 0,
  };
}

// Set team starting values based on division
export function setTeamStartValues(team: TeamData, divisionMoney: number): void {
  team.money = divisionMoney;
  team.rating = GAME_CONSTANTS.START_RATING;
  team.arenaPoints = GAME_CONSTANTS.START_ARENA_POINTS;
}

// Modify team money (tracks monthly balance)
export function modifyMoney(team: TeamData, amount: number): void {
  team.money += amount;
  team.currentMonthBalance += amount;
}

// Modify team rating (clamped 0-100)
export function modifyRating(team: TeamData, amount: number): void {
  team.rating = Math.max(0, Math.min(100, team.rating + amount));
}

// Modify arena points
export function modifyArenaPoints(team: TeamData, amount: number): void {
  team.arenaPoints += amount;
}

// Check if team is player controlled
export function isPlayerControlled(team: TeamData): boolean {
  return team.playerId !== null;
}

// Get battle team size
export function getBattleTeamSize(team: TeamData): number {
  return team.battleGladiatorIds.filter(id => id !== null).length;
}

// Check if battle team has gladiator
export function battleTeamHasGladiator(team: TeamData, gladiatorId: string): boolean {
  return team.battleGladiatorIds.includes(gladiatorId);
}

// Get next empty battle slot
export function getNextEmptyBattleSlot(team: TeamData): number {
  return team.battleGladiatorIds.findIndex(id => id === null);
}

// Get battle slot for gladiator
export function getBattleSlotForGladiator(team: TeamData, gladiatorId: string): number {
  return team.battleGladiatorIds.indexOf(gladiatorId);
}

// Add gladiator to battle slot
export function addToBattleSlot(team: TeamData, gladiatorId: string, slot: number): void {
  if (slot >= 0 && slot < GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE) {
    team.battleGladiatorIds[slot] = gladiatorId;
  }
}

// Remove gladiator from battle slot
export function removeFromBattleSlot(team: TeamData, slot: number): void {
  if (slot >= 0 && slot < GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE) {
    team.battleGladiatorIds[slot] = null;
  }
}

// Clear battle team
export function clearBattleTeam(team: TeamData): void {
  team.battleGladiatorIds = [null, null, null, null, null, null];
}

// Check if team dropped from cup (lost any cup battle)
export function droppedFromCup(team: TeamData): boolean {
  const cupMonths = [2, 4, 6, 8];
  return cupMonths.some(month => team.battleResults[month] === BATTLE_RESULT.LOSE);
}

// Get races of all gladiators in team (for one-per-race restriction)
export function getGladiatorRaces(team: TeamData, gladiators: Map<string, GladiatorData>): Race[] {
  const races: Race[] = [];
  for (const id of team.gladiatorIds) {
    const gladiator = gladiators.get(id);
    if (gladiator) {
      races.push(gladiator.race);
    }
  }
  return races;
}

// Check if team already has a gladiator of this race
export function hasRace(team: TeamData, race: Race, gladiators: Map<string, GladiatorData>): boolean {
  const races = getGladiatorRaces(team, gladiators);
  return races.some(r => r.id === race.id);
}

// Get count of healthy gladiators
export function getHealthyGladiatorCount(team: TeamData, gladiators: Map<string, GladiatorData>): number {
  let count = 0;
  for (const id of team.gladiatorIds) {
    const gladiator = gladiators.get(id);
    if (gladiator && isHealthy(gladiator)) {
      count++;
    }
  }
  return count;
}

// Calculate total salary for team
export function getTotalSalary(team: TeamData, gladiators: Map<string, GladiatorData>): number {
  let total = 0;
  for (const id of team.gladiatorIds) {
    const gladiator = gladiators.get(id);
    if (gladiator) {
      // Full salary if in battle team, half otherwise
      if (battleTeamHasGladiator(team, id)) {
        total += gladiator.salary;
      } else {
        total += Math.floor(gladiator.salary / 2);
      }
    }
  }
  return total;
}

// Reset for new month
export function resetTeamForNewMonth(team: TeamData): void {
  team.addedToBattle = false;
  team.currentMonthBalance = 0;
  team.currentMonthIncome = 0;
}

// Reset for new year
export function resetTeamForNewYear(team: TeamData): void {
  resetTeamForNewMonth(team);
  team.wins = 0;
  team.losses = 0;
  team.battleResults = new Array(12).fill(BATTLE_RESULT.NONE);
}

// Add gladiator to team
export function addGladiator(team: TeamData, gladiatorId: string): boolean {
  if (team.gladiatorIds.length >= GAME_CONSTANTS.MAX_GLADIATORS_IN_TEAM) {
    return false;
  }
  team.gladiatorIds.push(gladiatorId);
  return true;
}

// Remove gladiator from team
export function removeGladiator(team: TeamData, gladiatorId: string): void {
  const index = team.gladiatorIds.indexOf(gladiatorId);
  if (index !== -1) {
    team.gladiatorIds.splice(index, 1);
  }
  // Also remove from battle team if present
  const battleIndex = team.battleGladiatorIds.indexOf(gladiatorId);
  if (battleIndex !== -1) {
    team.battleGladiatorIds[battleIndex] = null;
  }
}
